// /src/components/gantt/components/task/TaskBar.jsx
import React, { useMemo, memo } from 'react';
import { Box, useTheme } from '@mui/material';
import TaskBarTooltip from './TaskBarTooltip';
import { getTaskColors } from '../../utils/taskColorUtils';
import { calculateTaskBarStyles } from '../../utils/taskBarStyles';
import { useDraggableTask } from '../../hooks/useDraggableTask';
import { useGanttDnd } from '../../context/GanttDndContext';

const TaskBarComponent = ({ task, category, level, visibleDayIndices, dayWidth, baseDate, onTaskClick }) => {
  const theme = useTheme();
  const { isEditMode, selectedTask, selectTaskForMove } = useGanttDnd();

  // Make this task draggable (skip ghost tasks and completed tasks)
  const isGhost = task.isGhost || false;
  const isCompleted = task.state === 'COMPLETED';
  const dragHandlers = useDraggableTask(task, category);
  const isDragging = false; // Native API doesn't provide isDragging state during drag
  const isSelected = isEditMode && selectedTask?.task.id === task.id && selectedTask?.category.id === category.id;

  // Get task colors - use gray for ghost tasks, original colors for real tasks
  const { taskColor, textColor } = getTaskColors(task, theme);

  // Memoize task bars calculation to avoid recalculating on every render
  const taskBars = useMemo(() => {
    return calculateTaskBarStyles(
      task,
      visibleDayIndices,
      dayWidth,
      baseDate,
      level,
      taskColor,
      textColor,
      isGhost,
      theme,
      onTaskClick,
      isEditMode,
      isCompleted
    );
  }, [task, visibleDayIndices, dayWidth, baseDate, level, taskColor, textColor, isGhost, theme, onTaskClick, isEditMode, isCompleted]);

  // Early return if no visible bars to render
  if (taskBars.length === 0) {
    return null;
  }

  const handleTaskClick = (event) => {
    if (isEditMode) {
      // Click-to-move: select/deselect this task instead of opening it
      if (isGhost || isCompleted) return;
      event.stopPropagation();
      selectTaskForMove(task, category);
      return;
    }

    event.stopPropagation();
    if (onTaskClick) {
      onTaskClick(task);
    }
  };

  return (
    <>
      {/* Task Bars with Lazy-Loaded Tooltip (disabled in edit mode) */}
      {taskBars.map((barStyle, barIndex) => {
        return (
          <TaskBarTooltip
            key={`${task.id}-${barIndex}`}
            task={task}
            isGhost={isGhost}
            isEditMode={isEditMode}
          >
            <Box
              {...(!isGhost ? dragHandlers : {})}
              sx={{
                ...barStyle,
                ...(isSelected && {
                  outline: `2px solid ${theme.palette.common.white}`,
                  boxShadow: `0 0 0 4px ${theme.palette.primary.main}`,
                  zIndex: (barStyle.zIndex || 1) + 1,
                  '&:hover': {
                    ...barStyle['&:hover'],
                    boxShadow: `0 0 0 4px ${theme.palette.primary.main}`,
                  },
                }),
                cursor: isEditMode && !isGhost && !isCompleted ? 'pointer' : barStyle.cursor,
              }}
              onClick={handleTaskClick}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  dispaly: 'flex',
                  alignContent: "center",
                }}
                title={isEditMode ? task.name : ""}
              >
                {task.name}
              </Box>
            </Box>
          </TaskBarTooltip>
        );
      })}
    </>
  );
};

// Memoize TaskBar to prevent unnecessary re-renders
const TaskBar = memo(TaskBarComponent, (prevProps, nextProps) => {
  return (
    prevProps.task.id === nextProps.task.id &&
    prevProps.task.startDate === nextProps.task.startDate &&
    prevProps.task.endDate === nextProps.task.endDate &&
    prevProps.level === nextProps.level &&
    prevProps.dayWidth === nextProps.dayWidth &&
    prevProps.baseDate === nextProps.baseDate &&
    prevProps.visibleDayIndices === nextProps.visibleDayIndices &&
    prevProps.onTaskClick === nextProps.onTaskClick
  );
});

TaskBar.displayName = 'TaskBar';

export default TaskBar;